import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import {
  cancelSubscription,
  cancelSubscriptionOverride,
  createSubscriptionOverride,
  getMySubscriptionDetail,
  listAddons,
  listSubscriptionOverrides,
  updateSubscriptionOverride,
  type ServiceAddonRow,
  type SubscriptionOverride,
} from '../../lib/booking-api';
import { customerApiErrorMessage } from '../../lib/customer-api';
import { toast } from '../../lib/toast';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function joinName(first: string | null, last: string | null): string {
  return [first, last].filter(Boolean).join(' ') || '—';
}

function cadenceLabel(freq: string | null | undefined): string {
  switch (freq) {
    case 'DAILY':
      return 'Every day';
    case 'WEEKLY':
      return 'Every week';
    case 'FORTNIGHTLY':
      return 'Every 2 weeks';
    case 'MONTHLY':
      return 'Every month';
    default:
      return 'Recurring';
  }
}

function statusTheme(status: string): { label: string; cls: string; dot: string } {
  switch (status) {
    case 'ACTIVE':
      return {
        label: 'Active',
        cls: 'bg-[#e6f7ec] text-[#1f7a3a] border border-[#bce5c9]',
        dot: '#1f7a3a',
      };
    case 'TRIALING':
      return {
        label: 'Trial running',
        cls: 'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
        dot: '#d48806',
      };
    case 'PAST_DUE':
      return {
        label: 'Payment past due',
        cls: 'bg-[#fde8e8] text-[#a8071a] border border-[#fab8b8]',
        dot: '#a8071a',
      };
    case 'CANCELLED':
      return {
        label: 'Cancelled',
        cls: 'bg-[#f4f4f4] text-[#3a3335] border border-[#ede8e9]',
        dot: '#908e8e',
      };
    case 'EXPIRED':
      return {
        label: 'Expired',
        cls: 'bg-[#f4f4f4] text-[#3a3335] border border-[#ede8e9]',
        dot: '#908e8e',
      };
    case 'INCOMPLETE':
    case 'INCOMPLETE_EXPIRED':
      return {
        label: 'Setup incomplete',
        cls: 'bg-[#fff7e6] text-[#9a6700] border border-[#fde2a1]',
        dot: '#d48806',
      };
    default:
      return {
        label: status,
        cls: 'bg-[#f4f4f4] text-[#3a3335] border border-[#ede8e9]',
        dot: '#908e8e',
      };
  }
}

// Pads a Date to YYYY-MM-DD in the local timezone (matches what the
// backend spawner uses when keying overrides).
const ymd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Walks forward from `from` to the next occurrence of `targetDow`
// (0 = Sun … 6 = Sat). Returns a fresh Date at midnight local.
const nextDow = (from: Date, targetDow: number): Date => {
  const out = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  while (out.getDay() !== targetDow) {
    out.setDate(out.getDate() + 1);
  }
  return out;
};

export default function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  // Overrides for upcoming visits, keyed by scheduled_date (YYYY-MM-DD)
  // so the per-visit cards can render the existing customer choice
  // without a per-row API call.
  const [overrides, setOverrides] = useState<SubscriptionOverride[]>([]);
  const [addonsCatalog, setAddonsCatalog] = useState<ServiceAddonRow[]>([]);

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getMySubscriptionDetail(id);
      setInfo(res.info);
      // Fire the override list + addon catalog requests after the
      // detail returns so they piggy-back on the same session refresh.
      try {
        const ov = await listSubscriptionOverrides(id);
        setOverrides(ov.data ?? []);
      } catch {
        /* non-blocking — the upcoming-visits card just hides edits */
      }
      try {
        const categoryId = res.info?.service_category?.id;
        const ad = await listAddons(categoryId);
        setAddonsCatalog(ad.data ?? []);
      } catch {
        /* non-blocking */
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not load your subscription.'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Compute the next 4 upcoming visit dates. Anchors on next_billing_at
  // when the backend has stamped it (most accurate — that's the moment
  // the spawner will fire). Otherwise falls back to "next time the
  // pinned day_of_week comes up locally" so the card still renders for
  // a brand-new sub that hasn't had its first invoice yet.
  const upcomingVisits = useMemo(() => {
    if (!info) return [] as Date[];
    const dow =
      typeof info.day_of_week === 'number' ? info.day_of_week : null;
    let cursor: Date | null = null;
    if (info.next_billing_at) {
      cursor = new Date(info.next_billing_at);
    } else if (dow !== null) {
      cursor = nextDow(new Date(), dow);
    }
    if (!cursor || Number.isNaN(cursor.getTime())) return [];
    const stepDays =
      info.frequency === 'DAILY'
        ? 1
        : info.frequency === 'WEEKLY'
          ? 7
          : info.frequency === 'FORTNIGHTLY'
            ? 14
            : info.frequency === 'MONTHLY'
              ? 28
              : 7;
    const out: Date[] = [];
    for (let i = 0; i < 4; i++) {
      const d = new Date(cursor.getTime() + i * stepDays * 24 * 60 * 60 * 1000);
      out.push(d);
    }
    return out;
  }, [info]);

  const overrideByDate = useMemo(() => {
    const m = new Map<string, SubscriptionOverride>();
    for (const o of overrides) {
      if (o.status === 'PENDING') m.set(o.scheduled_date, o);
    }
    return m;
  }, [overrides]);

  // Reload overrides on save without re-running the whole detail call.
  async function refreshOverrides() {
    if (!id) return;
    try {
      const ov = await listSubscriptionOverrides(id);
      setOverrides(ov.data ?? []);
    } catch {
      /* non-blocking */
    }
  }

  async function handleCancel() {
    if (!info) return;
    if (
      !confirm(
        'Cancel this subscription? You won\'t be charged after the current cycle ends.',
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      await cancelSubscription(info.id, { at_period_end: true });
      toast.success('Subscription cancelled.');
      await load();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not cancel your subscription.'),
      );
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <MainHeader />
        <main className="flex-1 max-w-[800px] mx-auto px-4 py-16 w-full">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-1/2 bg-[#f0eded] rounded" />
            <div className="h-4 w-1/3 bg-[#f5f1f2] rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="border border-[#ede8e9] rounded-[10px] bg-white p-5 space-y-3"
                >
                  <div className="h-3 w-1/3 bg-[#f0eded] rounded" />
                  <div className="h-4 w-3/4 bg-[#f5f1f2] rounded" />
                  <div className="h-4 w-1/2 bg-[#f5f1f2] rounded" />
                </div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <MainHeader />
        <main className="flex-1 max-w-[800px] mx-auto px-4 py-16 text-center w-full">
          <span className="material-icons text-[48px] text-[#eee] mb-3">
            event_busy
          </span>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] mb-2">
            Subscription not found
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] mb-6">
            We couldn't find this subscription.
          </p>
          <PrimaryButton onClick={() => navigate('/bookings')}>
            Back to bookings
          </PrimaryButton>
        </main>
        <Footer />
      </div>
    );
  }

  const theme = statusTheme(info.status);
  const cadence = cadenceLabel(info.frequency);
  const proUser = info.preferred_professional?.user;
  const proName =
    joinName(proUser?.first_name ?? null, proUser?.last_name ?? null) ||
    info.preferred_professional?.business_name ||
    '';
  const dayLabel =
    typeof info.day_of_week === 'number'
      ? DAY_NAMES[info.day_of_week] ?? ''
      : '';
  const startTime = info.start_time ? String(info.start_time).slice(0, 5) : '';
  const address = [info.address_line1, info.address_line2, info.city, info.postcode]
    .filter(Boolean)
    .join(', ');
  const isLive =
    info.status === 'ACTIVE' ||
    info.status === 'TRIALING' ||
    info.status === 'PAST_DUE';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 pb-16 w-full">
        <div className="max-w-[800px] mx-auto px-4 py-8">
          <button
            type="button"
            onClick={() => navigate('/bookings')}
            className="inline-flex items-center gap-1 mb-4 text-sm text-[#908e8e] hover:text-[#8e2a2f]"
          >
            <span className="material-icons text-[18px]">navigate_before</span>
            Back to bookings
          </button>

          <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
            <div className="flex-1 min-w-0">
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px] mb-1 truncate">
                {info.service?.name ?? 'Subscription'}
              </h1>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
                {cadence}
                {dayLabel ? ` · ${dayLabel}` : ''}
                {startTime ? ` at ${startTime}` : ''}
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 h-[28px] px-3 rounded-full font-['DM_Sans',sans-serif] text-[12px] font-bold ${theme.cls}`}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: theme.dot }}
              />
              {theme.label}
            </span>
          </div>

          {info.created_at ? (
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-6">
              Booked on {formatDate(info.created_at)}
            </p>
          ) : null}

          {isLive && !info.cancel_at_period_end ? (
            <div className="mb-8">
              <SecondaryButton
                onClick={handleCancel}
                disabled={cancelling}
                className="!h-[44px] px-5"
              >
                {cancelling ? 'Cancelling…' : 'Cancel subscription'}
              </SecondaryButton>
            </div>
          ) : info.cancel_at_period_end ? (
            <div className="mb-8 p-4 rounded-[10px] bg-[#fff7e6] border border-[#fde2a1]">
              <p className="font-['DM_Sans',sans-serif] text-[#9a6700] text-[13px]">
                You've cancelled this subscription. It stays active until the
                end of the current cycle, then stops billing automatically.
              </p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card title="Billing">
              <KV
                label="Per cycle"
                value={formatMoney(
                  Number(info.amount_per_cycle ?? 0),
                  info.currency || 'GBP',
                )}
              />
              <KV label="Cadence" value={cadence} />
              {info.next_billing_at ? (
                <KV
                  label="Next billing"
                  value={formatDate(info.next_billing_at)}
                />
              ) : null}
            </Card>

            <Card title="Schedule">
              {dayLabel ? <KV label="Day" value={dayLabel} /> : null}
              {startTime ? <KV label="Start time" value={startTime} /> : null}
              {info.duration_minutes ? (
                <KV
                  label="Duration"
                  value={`${info.duration_minutes} min`}
                />
              ) : null}
            </Card>

            <Card title="Professional">
              <KV label="Name" value={proName} />
              {proUser?.email ? (
                <KV label="Email" value={proUser.email} />
              ) : null}
              {/* Message action — opens the in-app chat with this pro, so
                  recurring customers can reach their regular pro the same
                  way one-off bookings already allow. */}
              {proUser?.id ? (
                <button
                  type="button"
                  onClick={() =>
                    navigate('/messenger', {
                      state: { openWithUserId: proUser.id },
                    })
                  }
                  className="mt-3 inline-flex items-center gap-1.5 h-[40px] px-4 rounded-[10px] bg-[#8e2a2f] hover:bg-[#7a2228] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] transition-colors"
                >
                  <span className="material-icons text-[18px]">
                    chat_bubble_outline
                  </span>
                  Message
                </button>
              ) : null}
            </Card>

            <Card title="Service address">
              <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
                {address || '—'}
              </p>
            </Card>
          </div>

          {/* Per-occurrence add-on overrides. Lets the customer swap
              ironing for a different add-on (or drop it entirely) on a
              specific upcoming visit without touching the recurring
              schedule. The cycle's Stripe billing amount doesn't
              change — overrides only affect what the pro does on the
              day. */}
          {upcomingVisits.length > 0 ? (
            <div className="mt-6 border border-[#ede8e9] rounded-[10px] bg-white p-5">
              <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] mb-1">
                Manage upcoming visits
              </h2>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mb-4">
                Need to swap or drop the extras for a one-off visit? Edit a
                specific date below. Your regular schedule stays the same.
              </p>
              <div className="space-y-3">
                {upcomingVisits.map((d) => (
                  <UpcomingVisitRow
                    key={ymd(d)}
                    date={d}
                    override={overrideByDate.get(ymd(d)) ?? null}
                    addons={addonsCatalog}
                    subscriptionId={info.id}
                    onSaved={refreshOverrides}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function UpcomingVisitRow({
  date,
  override,
  addons,
  subscriptionId,
  onSaved,
}: {
  date: Date;
  override: SubscriptionOverride | null;
  addons: ServiceAddonRow[];
  subscriptionId: string;
  onSaved: () => Promise<void>;
}) {
  const dateYmd = (() => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Map<string, number>>(() => {
    const m = new Map<string, number>();
    for (const it of override?.items_override ?? []) {
      m.set(it.service_addon_id, it.quantity);
    }
    return m;
  });

  // Re-sync local state to incoming override whenever the parent
  // refreshes — otherwise an external save wouldn't reflect.
  useEffect(() => {
    const m = new Map<string, number>();
    for (const it of override?.items_override ?? []) {
      m.set(it.service_addon_id, it.quantity);
    }
    setSelected(m);
  }, [override]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 1);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const items = Array.from(selected.entries()).map(([id, qty]) => ({
        service_addon_id: id,
        quantity: qty,
      }));
      if (override) {
        await updateSubscriptionOverride(override.id, { items });
        toast.success('Extras updated for this visit.');
      } else {
        await createSubscriptionOverride(subscriptionId, {
          scheduled_date: dateYmd,
          items,
        });
        toast.success('Extras updated for this visit.');
      }
      setEditing(false);
      await onSaved();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not save your changes.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!override) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await cancelSubscriptionOverride(override.id);
      toast.success('Back to your regular extras for this visit.');
      setEditing(false);
      await onSaved();
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not cancel this override.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const overrideAddonLabels =
    override && addons.length > 0
      ? override.items_override
          .map((i) => addons.find((a) => a.id === i.service_addon_id)?.name)
          .filter((s): s is string => !!s)
      : [];

  return (
    <div className="border border-[#ede8e9] rounded-[8px] p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px]">
            {date.toLocaleDateString(undefined, {
              weekday: 'long',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}
          </p>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
            {override
              ? overrideAddonLabels.length > 0
                ? `Custom extras: ${overrideAddonLabels.join(', ')}`
                : 'No extras for this visit (override active)'
              : 'Uses your regular extras.'}
          </p>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[13px] underline"
          >
            Edit extras
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-4 border-t border-[#ede8e9] pt-3 space-y-2">
          {addons.length === 0 ? (
            <p className="text-[#908e8e] text-[13px]">
              No add-ons available for this category.
            </p>
          ) : (
            addons.map((a) => {
              const isOn = selected.has(a.id);
              return (
                <label
                  key={a.id}
                  className="flex items-center justify-between gap-3 cursor-pointer py-1"
                >
                  <span className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => toggle(a.id)}
                      className="h-4 w-4 rounded border-[#ede8e9] text-[#8e2a2f]"
                    />
                    <span className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px]">
                      {a.name}
                    </span>
                  </span>
                  <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
                    £{Number(a.price ?? 0).toFixed(2)}
                  </span>
                </label>
              );
            })
          )}
          <div className="flex flex-wrap gap-2 pt-3">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] rounded-[8px] px-4 h-[36px] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {override ? (
              <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="border border-[#ede8e9] text-[#3a3335] font-['DM_Sans',sans-serif] font-bold text-[13px] rounded-[8px] px-4 h-[36px] disabled:opacity-50"
              >
                Use my regular extras
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="text-[#908e8e] font-['DM_Sans',sans-serif] font-bold text-[13px] px-4 h-[36px] disabled:opacity-50"
            >
              Discard
            </button>
          </div>
          <p className="text-[#908e8e] text-[11px] pt-2">
            Note: your subscription is still billed at the same per-cycle amount — overrides only change what the pro does on the visit.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-[#ede8e9] rounded-[10px] bg-white p-5">
      <h2 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[12px] mb-3 uppercase tracking-wide">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-[#908e8e]">{label}</span>
      <span className="text-[#3a3335] text-right max-w-[60%] truncate">
        {value || '—'}
      </span>
    </div>
  );
}
